/**
 * Cross-lane render contracts (M1 asset contract §8.2). Gameplay-owned and FROZEN for M1:
 * both lanes compile against these names. Additive changes only, through the merge agent.
 */

export type QualityPreset = 'low' | 'medium' | 'high'
export const QUALITY_PRESETS: readonly QualityPreset[] = ['low', 'medium', 'high']

/** Operator liveries (style guide §5.4): the four M1 ids, then the later factions (M4 E6, spec §17), then the player corporation (M6 P23). */
export type FactionId =
  | 'player_lease'
  | 'raider'
  | 'first_rush'
  | 'neutral'
  | 'authority'
  | 'tethys'
  | 'meridian'
  | 'varro_security'
  | 'mutual'
  | 'holdfast'
  | 'guardian'
  | 'player_corp'
export const FACTION_IDS: readonly FactionId[] = [
  'player_lease',
  'raider',
  'first_rush',
  'neutral',
  'authority',
  'tethys',
  'meridian',
  'varro_security',
  'mutual',
  'holdfast',
  'guardian',
  'player_corp',
]

/** One live-tunable value. The dev panel binds `value` and calls the group's `apply()` after a change. */
export interface TuningParam {
  value: number | boolean | string
  min?: number
  max?: number
  step?: number
}

/** A named set of tuning values exposed by a render module (materials, post, environment). */
export interface TuningGroup {
  readonly name: string
  readonly params: Record<string, TuningParam>
  apply(): void
}

/**
 * Particle pools (gameplay) render one `Mesh` per VFX material: an `InstancedBufferGeometry`
 * whose base is a unit quad (4 vertices at (±0.5, ±0.5, 0), `uv`, 2 triangles) plus these
 * per-instance `InstancedBufferAttribute`s, with `instanceCount` = live particles.
 * The mesh sits at the render origin with an identity transform and `frustumCulled = false`.
 * VFX materials (asset lane) billboard or stretch the quad in the vertex stage from these attributes.
 */
export const PARTICLE_ATTR = {
  /** vec3: particle centre in render space (metres, camera-relative, D-011). Rewritten every frame. */
  pos: 'aPos',
  /** float: normalized age 0..1 (0 = birth, 1 = death). */
  age: 'aAge',
  /** float: per-particle random 0..1, fixed for its life. */
  seed: 'aSeed',
  /** vec3: linear colour multiplier, (1, 1, 1) = the material's own colour. */
  tint: 'aTint',
  /** vec2: x = size (width) in metres; y = streak length in metres along `aDir` (0 = round billboard). */
  size: 'aSize',
  /** vec3: unit direction in render space (velocity for streaks and sparks, surface normal for impacts). */
  dir: 'aDir',
} as const

export const PARTICLE_ATTR_SIZES: Readonly<Record<keyof typeof PARTICLE_ATTR, 1 | 2 | 3>> = {
  pos: 3,
  age: 1,
  seed: 1,
  tint: 3,
  size: 2,
  dir: 3,
}

/**
 * Asteroid instancing (gameplay). One `InstancedMesh` per (archetype, LOD) using the kit's geometry
 * and material. `instanceMatrix` holds field-local transforms (the mesh itself is placed in render
 * space each frame from the float64 field origin), plus these per-instance attributes on the geometry clone.
 */
export const ASTEROID_ATTR = {
  /** float: per-rock random 0..1 (tint and surface variation). */
  seed: 'aSeed',
  /** vec4: xyz = unit spin axis (instance-local), w = spin rate in rad/s (0 = static). Visual only. */
  spin: 'aSpin',
} as const

/** Particle material keys (VfxMaterials). */
export type VfxMaterialKey = 'explosion' | 'spark' | 'muzzle' | 'trail' | 'flash' | 'shieldHit' | 'tracerPdc' | 'boltLaser'
export const VFX_MATERIAL_KEYS: readonly VfxMaterialKey[] = [
  'explosion',
  'spark',
  'muzzle',
  'trail',
  'flash',
  'shieldHit',
  'tracerPdc',
  'boltLaser',
]

/** Effects the gameplay lane can trigger. Each one is a recipe of emitters (asset-lane data). */
export type VfxEffectId =
  | 'explosionSmall'
  | 'explosionLarge'
  | 'hitHull'
  | 'hitShield'
  | 'muzzlePdc'
  | 'muzzleLaser'
  | 'torpedoLaunch'
  | 'torpedoTrail'
export const VFX_EFFECT_IDS: readonly VfxEffectId[] = [
  'explosionSmall',
  'explosionLarge',
  'hitHull',
  'hitShield',
  'muzzlePdc',
  'muzzleLaser',
  'torpedoLaunch',
  'torpedoTrail',
]

/**
 * One burst of particles. The gameplay particle system executes it generically: `count` particles,
 * each with a random life, size, speed and direction inside `coneDeg` around the effect's direction.
 */
export interface VfxEmitterSpec {
  readonly material: VfxMaterialKey
  /** Particles per trigger (trails: particles per second while active). */
  readonly count: number
  /** Seconds [min, max]. */
  readonly life: readonly [number, number]
  /** Width in metres at birth [min, max]. */
  readonly size: readonly [number, number]
  /** Streak length in metres [min, max]; [0, 0] = round billboard. */
  readonly stretch: readonly [number, number]
  /** Metres per second [min, max]. */
  readonly speed: readonly [number, number]
  /** Half-angle in degrees around the effect direction; 180 = full sphere. */
  readonly coneDeg: number
  /** Share of the source's velocity inherited, 0..1. */
  readonly inheritVelocity: number
  /** Linear velocity damping per second. */
  readonly drag: number
  /** Linear tint multiplier. */
  readonly tint: readonly [number, number, number]
}

export type VfxRecipeBook = Readonly<Record<VfxEffectId, readonly VfxEmitterSpec[]>>
